import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useLazyQuery, gql } from "@apollo/client";
import { PatientBanner } from "./PatientBanner";
import { Sidebar, sidebarItems } from "./Sidebar";
import { 
  Loader2, 
  AlertTriangle,
  Building,
  Users
} from "lucide-react";

import { useUser } from "@/store/useUser"; // Ensure this path is correct based on project structure

// Import existing modules
import { Timeline } from "./timeline/Timeline";
import { Scans } from "./scans/Scans";
import { Enrollment } from "./enrollment/Enrollment";
import { LegalStatus } from "./legalstatus/LegalStatus";
import { MedicalStatus } from "./medicalstatus/MedicalStatus";
import { PlanAndLocation } from "./planandlocation/PlanAndLocation";
import { Notifications } from "./notifications/Notifications";
import { CalAIM } from "./calaim/CalAIM";
import { Notes } from "./notes/Notes";
import { Incidents } from "./incidents/Incidents";
import { CoordinatedCare } from "./coordinatedcare/CoordinatedCare";
import { OutreachTab } from "./outreach/OutreachTab";
import { ReferenceBrowserTab } from "./reference/ReferenceBrowserTab";
import { ParticipantDataSectionFull } from "./shared/components/ParticipantDataSectionFull";
import { ProgramDetailsGrid } from "./shared/components/ProgramDetailsGrid";
import { EditParticipant } from "./shared/modals/EditParticipant";
import { EditProgram } from "./shared/modals/EditProgram";
import { Summary } from "./Summary";
import { AddEntryModal, EntryType } from "./add_entry";
import { TabHeader } from "./shared/components/TabHeader";
import { DocumentsScans } from "./DocumentsScans";
import { CandidateDetails } from "./CandidatesData";
import { MissingItems } from "./missingitems/MissingItems";
import { ProgressReportsTab } from "./progressreports/ProgressReportsTab";
import { MedcallTab } from "./medcall/MedcallTab";
import { cn } from "@/lib/utils";
import { useUserPermissions } from "@/store/useUserPermissions";
import { hasAnyPolicy } from "@/utils/policies";

// Define Queries
const GET_PARTICIPANT_BY_ODR_NUMBER = gql`
  query GetParticipantByODRNumber($odrnumber: Int!) {
    getParticipantByODRNumber(odrnumber: $odrnumber) {
      id
      rso
      pcodeHistory { date pcode }
      candidatesData { id firstName lastName }
      scans { id filename filetype filelabel filedate }
      nameHistory { lastName firstName program entrydate }
      name { firstName lastName }
      dob
      dobHistory { value program entrydate }
      programs
      mrun
      mrunHistory { value program entrydate }
      champid
      champidHistory { value program entrydate }
      genderHistory { value program entrydate }
      isNumber
      isNumberHistory { value program entrydate }
      disposition { disposition }
      dispositionHistory { disposition dispositionDate program }
      pathwayHistory { value program entrydate }
      providerHistory { value program entrydate }
      releasedDate 
      # releaseDate # Temporarily commented out due to GraphQL schema mismatch
      releasedDateHistory { value program }
      jcitHistory { value program entrydate }
      icd10
      icd10History { value program entrydate }
      race
      probationHistory { value program entrydate }
      programDetails {
        programDescription
        programParticipantNumber
        ab109
        commitmentDate
        completionDate
        dischargeDate
        disposition
        entryDate
        exitDate
        maxDate
        istStatus
        pathway
        provider
        releaseDate
        probation
        jcit
        active
      }
      fin
      probation      
      cii
      cin
      cinHistory { value program entrydate }
      ssn
      healthplan { healthplan entrydate }
      medicalProblems
      psychotropicMeds
      otherMeds
	  currentMeds
	  dsm5dx
      clinicalPresentation
      psychHistory
      substanceHistory
      
      # New fields for Banner Config
      acuity
      arrestDate
      booking
      bookingNumber
      mainNumber
      # lastInitialAssessmentDate { entrydate } # Temporarily commented out due to GraphQL schema mismatch
      # lastLACAREReferralDate { entrydate } # Temporarily commented out due to GraphQL schema mismatch
      # lastLACAREScreeningDate { entrydate } # Temporarily commented out due to GraphQL schema mismatch
      
      # Key Events
      keyEvents {
        uniqueid
        startdate
        enddate
        event
        notes
        entryby
        entrydate
      }
    }
  }
`;

const PARTICIPANT_TAB_POLICY_REQUIREMENTS: Record<string, string[]> = {
  missingitems: ["program:participant:missingitems~full"],
  candidates: ["program:candidate~full"],
  coordinatedcare: ["program:participant:coordinatedcare~full"],
  outreach: ["program:participant:outreach~full"],
  referencebrowser: ["program:participant:referencebrowser~full"],
  documents: ["program:participant:documentscans~read"],
  documentscans: ["program:participant:documentscans~read"],
  enrollment: ["program:participant:enrollment~full"],
  incidents: ["program:participant:incidents~full"],
  legal: ["program:participant:legal~full"],
  medical: ["program:participant:medical~full"],
  medcall: ["tools:medcall:access:~user", "tools:medcall:access:~full", "admin:access~full"],
  notes: ["program:participant:notes~full"],
  notifications: ["program:participant:notifications~full"],
  disposition: ["program:participant:disposition~full"],
  programs: ["program:participant:programs~full"],
  scans: ["program:participant:scans~full"],
  progressreports: ["tools:progressreportaccess~full"]
};

const PARTICIPANT_SUMMARY_POLICY_REQUIREMENTS = [
  "program:participant~read",
  "program:participant~read",
  "program:participant~read",
  "program:participant~read",
  "admin:access~full",
  "admin:access~full"
];

export const ParticipantView = () => {
  const { participant } = useParams<{ participant: string }>();
  const navigate = useNavigate();
  const { user, recordRecentParticipantVisit } = useUser();
  const { policies } = useUserPermissions();
  
  // Default to summary
  const [activeTab, setActiveTab] = useState("summary");
  const [selectedProgram, setSelectedProgram] = useState<any>(null);
  const [selectedProgramForModal, setSelectedProgramForModal] = useState<string | null>(null);
  
  // Edit States
  const [editParticipant, setEditParticipant] = useState(false);
  const [addEntryModalOpen, setAddEntryModalOpen] = useState(false);
  const [addEntryModalInitialType, setAddEntryModalInitialType] = useState<EntryType>("");
  const [editProgram, setEditProgram] = useState<any>({
    show: false,
    program: {},
    active: false
  });

  // Participant Data Query
  const { loading, error, data, refetch } = useQuery(GET_PARTICIPANT_BY_ODR_NUMBER, {
    variables: { odrnumber: parseInt(participant || "0") },
    skip: !participant,
    onCompleted: (data) => {
        const allPrograms = data?.getParticipantByODRNumber?.programDetails || [];
        const activePrograms = allPrograms.filter((p: any) => p.active);
        if (activePrograms.length > 0) {
            setSelectedProgram(activePrograms[0]);
        } else if (allPrograms.length > 0) {
            setSelectedProgram(allPrograms[0]);
        }
    }
  });

  useEffect(() => {
    if (!participant) return;
    if (!data?.getParticipantByODRNumber) return;

    const odrNumber = parseInt(participant, 10);
    if (Number.isNaN(odrNumber)) return;

    const participantData = data.getParticipantByODRNumber;
    const displayNameSource = participantData.name || participantData.candidatesData || {};
    const nameParts = [];

    if (displayNameSource.firstName) {
      nameParts.push(displayNameSource.firstName);
    }

    if (displayNameSource.lastName) {
      nameParts.push(displayNameSource.lastName);
    }

    const fallbackName = `Participant ${odrNumber}`;
    const participantName = nameParts.join(' ').trim() || fallbackName;

    recordRecentParticipantVisit({
      participantId: typeof participantData.id === 'number' ? participantData.id : null,
      odrNumber,
      name: participantName
    });
  }, [data, participant, recordRecentParticipantVisit]);

  const canAccessTab = React.useCallback(
    (tabId: string) => {
      const normalizedTab = tabId === "documents" ? "documentscans" : tabId;
      if (normalizedTab === "summary") {
        return hasAnyPolicy(policies, PARTICIPANT_SUMMARY_POLICY_REQUIREMENTS);
      }

      const requiredPolicies = PARTICIPANT_TAB_POLICY_REQUIREMENTS[normalizedTab];
      if (!Array.isArray(requiredPolicies) || requiredPolicies.length === 0) {
        return true;
      }

      return hasAnyPolicy(policies, requiredPolicies);
    },
    [policies]
  );

  const visibleSidebarItems = React.useMemo(
    () => sidebarItems.filter((item) => canAccessTab(item.id)),
    [canAccessTab]
  );
  const fallbackTab = visibleSidebarItems[0]?.id || "summary";

  useEffect(() => {
    if (activeTab === "documents") {
      if (canAccessTab("documentscans")) {
        setActiveTab("documentscans");
      } else {
        setActiveTab(fallbackTab);
      }
      return;
    }

    if (!canAccessTab(activeTab)) {
      setActiveTab(fallbackTab);
    }
  }, [activeTab, canAccessTab, fallbackTab]);

  const renderScrollableTab = (children: React.ReactNode, className?: string) => (
    <div className={cn("h-full overflow-y-auto px-4 py-4", className)}>
      {children}
    </div>
  );

  const renderContent = () => {
    if (!participant) {
      return (
        <div className="flex h-full items-center justify-center text-[var(--neutral-gray-80)]">
          <div className="text-center">
            <p className="text-lg font-medium text-[var(--neutral-gray-120)]">No participant selected</p>
            <p className="text-sm">Please select a participant to view their details.</p>
          </div>
        </div>
      );
    }

    if (loading) {
        return (
            <div className="flex h-full items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-[var(--azure-primary)]" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex h-full items-center justify-center text-[var(--semantic-error)]">
                <div className="text-center">
                    <AlertTriangle className="h-12 w-12 mx-auto mb-4" />
                    <p>Error loading participant data.</p>
                </div>
            </div>
        );
    }

    const odrNum = parseInt(participant);
    const progNum = selectedProgram?.id || selectedProgram?.programParticipantNumber || "0"; 
    const progName = selectedProgram?.programDescription || "";

    if (!canAccessTab(activeTab)) {
      return (
        <div className="flex h-full items-center justify-center text-[var(--neutral-gray-80)]">
          <div className="text-center">
            <p className="text-lg font-medium text-[var(--neutral-gray-120)]">Access denied</p>
            <p className="text-sm">You do not have access to this section.</p>
          </div>
        </div>
      );
    }

    switch (activeTab) {
      case 'summary':
        return (
            <div className="h-full overflow-hidden">
                <Summary
                  odrnumber={odrNum}
                  onOpenMedcall={canAccessTab('medcall') ? () => setActiveTab('medcall') : undefined}
                  onOpenProgressReports={() => setActiveTab('progressreports')}
                />
            </div>
        );
      case 'missingitems':
        return renderScrollableTab(
          <MissingItems participantData={data?.getParticipantByODRNumber} />
        );
      case 'timeline':
        return renderScrollableTab(
          <div className="w-full">
            <ParticipantDataSectionFull odrnumber={odrNum} />
          </div>,
          "flex gap-4"
        );
      case 'programs':
        return renderScrollableTab(
          <>
            <TabHeader
              icon={Building}
              title="Programs"
              subtitle="Program assignments and status"
            />
            <ProgramDetailsGrid
              data={data}
              odrnumber={odrNum}
              color="bg-gray-50"
              selectedProgram={selectedProgram}
              setSelectedProgram={setSelectedProgram}
              refetch={refetch}
              selectedProgramForModal={selectedProgramForModal}
              setSelectedProgramForModal={setSelectedProgramForModal}
            />
          </>,
          "space-y-4"
        );
      case 'scans':
        return renderScrollableTab(
          <Scans odrnumber={odrNum} scans={data?.getParticipantByODRNumber?.scans || []} loading={loading} error={error} />
        );
      case 'documents':
      case 'documentscans':
        return (
          <div className="h-full overflow-hidden">
            <DocumentsScans
              odrnumber={odrNum}
              scans={data?.getParticipantByODRNumber?.scans || []}
              loading={loading}
              error={error}
            />
          </div>
        );
      case 'enrollment':
        return renderScrollableTab(<Enrollment programNumber={odrNum} />);
      case 'legal':
        return renderScrollableTab(<LegalStatus odrNumber={odrNum} programNumber={parseInt(progNum)} />);
      case 'medical':
        return renderScrollableTab(<MedicalStatus odrnumber={odrNum} data={data} refetch={refetch} />);
      case 'medcall':
        return renderScrollableTab(<MedcallTab odrparticipant={odrNum} />);
      case 'disposition':
        return renderScrollableTab(<PlanAndLocation odrNumber={odrNum} programNumber={parseInt(progNum)} />);
      case 'notifications':
        return renderScrollableTab(
          <Notifications
            programName={selectedProgram?.programDescription || progName}
            odrNumber={odrNum}
            programParticipantNumber={progNum}
            programDetails={data?.getParticipantByODRNumber?.programDetails || []}
          />
        );
      case 'calaim':
        return renderScrollableTab(<CalAIM odrNumber={odrNum} programNumber={parseInt(progNum)} />);
      case 'notes':
        return renderScrollableTab(<Notes odrnumber={odrNum} programNumber={progNum} />);
      case 'incidents':
        return renderScrollableTab(<Incidents programNumber={odrNum} />);
      case 'coordinatedcare':
        return renderScrollableTab(<CoordinatedCare odrNumber={odrNum} programNumber={parseInt(progNum)} />);
      case 'outreach':
        return renderScrollableTab(
          <OutreachTab
            odrnumber={odrNum}
            participantId={data?.getParticipantByODRNumber?.id}
          />
        );
      case 'referencebrowser':
        return renderScrollableTab(
          <ReferenceBrowserTab odrnumber={odrNum} />
        );
      case 'candidates':
        return renderScrollableTab(
          <>
            <TabHeader
              icon={Users}
              title="Candidate List Entry"
              subtitle="Candidate list entry details"
            />
            <CandidateDetails odrNumber={odrNum} />
          </>,
          "space-y-4"
        );
      case 'progressreports': {
        const participantData = data?.getParticipantByODRNumber;
        const displayName = [participantData?.name?.firstName, participantData?.name?.lastName].filter(Boolean).join(' ');
        return (
          <ProgressReportsTab
            participantId={Number(progNum || 0)}
            odrparticipant={odrNum}
            cid={Number(participantData?.champid || 0) || null}
            clientName={displayName}
          />
        );
      }
      default:
        return renderScrollableTab(
          <div className="text-sm text-[var(--neutral-gray-80)]">Select a module</div>
        );
    }
  };

  const odrNum = participant ? parseInt(participant) : 0;
  const progNum = selectedProgram?.id || selectedProgram?.programParticipantNumber || "0"; 

  return (
    <div data-testid="participant-page-root" className="flex h-screen flex-col overflow-hidden bg-[var(--neutral-gray-10)]">
      <div className="px-0 pb-3 pt-1 sm:px-1 lg:px-1 xl:px-2">
        <PatientBanner 
          data={data} 
          loading={loading} 
          selectedProgram={selectedProgram}
          onEditParticipant={() => setEditParticipant(true)}
          onAddEntry={(entryType?: EntryType) => {
            setAddEntryModalInitialType(entryType || "");
            setAddEntryModalOpen(true);
          }}
        />
      </div>

      <div className="flex min-h-0 flex-1 gap-3 px-0 pb-3 sm:px-1 lg:px-1 xl:px-2">
        <Sidebar activeTab={activeTab} onTabChange={setActiveTab} items={visibleSidebarItems} />

        <div data-testid="participant-tab-content" className="app-page-shell relative flex min-h-0 flex-1 flex-col overflow-hidden bg-[var(--surface-panel-solid)]">
          {renderContent()}
        </div>
      </div>

      {editParticipant && selectedProgram && (
        <EditParticipant
          isOpen={editParticipant}
          onClose={() => setEditParticipant(false)}
          program={selectedProgram.programDescription || 'odr'} // Fallback if null
          odrnumber={odrNum}
          data={data}
          isActive={true}
          refetch={refetch}
        />
      )}

      {editProgram?.show && (
        <EditProgram
          isOpen={editProgram.show}
          onClose={() => setEditProgram({ show: false, program: null, active: false })}
          program={editProgram.program}
          odrnumber={odrNum}
          data={data}
          programOnly
          active={editProgram.active}
          refetch={refetch}
        />
      )}
      
      {addEntryModalOpen && (
        <AddEntryModal
            isOpen={addEntryModalOpen}
            onClose={() => {
              setAddEntryModalOpen(false);
              setAddEntryModalInitialType("");
            }}
            odrNumber={odrNum.toString()}
            participantId={progNum}
            user={user}
            refetch={refetch}
            initialEntryType={addEntryModalInitialType}
        />
      )}
    </div>
  );
};

export default ParticipantView;
